import { formatStableIdDebugLabel } from '../graph/debugLabels.js';
import {
  findLayerGraphLibraryItem,
  resolveGeometrySlot as resolveGeometrySlotFromLibraries,
  resolveMaterialSlot as resolveMaterialSlotFromLibraries,
  type GeometrySlot,
  type LayerGraphLibraries,
  type MaterialSlot,
  type ResolvedGraph,
} from '../scene/layerState.js';
import type { AppState } from './appState.js';

export function getLayerGraphLibrariesFor(appState: AppState): LayerGraphLibraries {
  return {
    geometryLibrary: appState.geometryLibrary,
    materialLibrary: appState.materialLibrary,
  };
}

export function resolveGeometrySlotFor(appState: AppState, slot: GeometrySlot): ResolvedGraph | null {
  const resolved = resolveGeometrySlotFromLibraries(getLayerGraphLibrariesFor(appState), slot);
  if (!resolved && slot.type === 'library') {
    console.warn(`geometry library item not found: ${formatStableIdDebugLabel(slot.id)}`);
  }
  return resolved;
}

export function resolveMaterialSlotFor(appState: AppState, slot: MaterialSlot): ResolvedGraph | null {
  const resolved = resolveMaterialSlotFromLibraries(getLayerGraphLibrariesFor(appState), slot);
  if (!resolved && slot.type === 'library') {
    console.warn(`material library item not found: ${formatStableIdDebugLabel(slot.id)}`);
  }
  return resolved;
}

/**
 * Render-path resolve cache. `resolveSlot` clones nodes/edges on every call
 * (editor flows need private copies), but renderers only read the graphs and
 * rely on stable object identity for dirty-rect diffing — so cache the clone
 * per source object (library item or inline slot). Sources are replaced
 * immutably on edit, which invalidates the cache entry naturally.
 */
const renderGraphCache = new WeakMap<object, ResolvedGraph>();

function resolveSlotForRender(
  appState: AppState,
  slot: GeometrySlot | MaterialSlot,
  kind: 'geometry' | 'material',
): ResolvedGraph | null {
  const source: object | null = slot.type === 'library'
    ? findLayerGraphLibraryItem(getLayerGraphLibrariesFor(appState), kind, slot.id) ?? null
    : slot;
  if (source) {
    const cached = renderGraphCache.get(source);
    if (cached) return cached;
  }
  const resolved = kind === 'geometry'
    ? resolveGeometrySlotFor(appState, slot as GeometrySlot)
    : resolveMaterialSlotFor(appState, slot as MaterialSlot);
  if (source && resolved) renderGraphCache.set(source, resolved);
  return resolved;
}

export function resolveGeometrySlotForRender(appState: AppState, slot: GeometrySlot): ResolvedGraph | null {
  return resolveSlotForRender(appState, slot, 'geometry');
}

export function resolveMaterialSlotForRender(appState: AppState, slot: MaterialSlot): ResolvedGraph | null {
  return resolveSlotForRender(appState, slot, 'material');
}
